/**
 * Shared handling for the query parameters that pre-populate the match widget.
 *
 * Anchor links across articles and program pages carry the visitor's context
 * forward, so the widget opens with the degree level, area of study, and
 * program already selected instead of asking again.
 *
 *   ?degreeInterest=4&areaStudy=1&mProgram=102
 *
 * Values match the option `value` strings in `data/degrees.ts`:
 *   degreeInterest -> degreeInterestOptions
 *   areaStudy      -> focusOptions
 *   mProgram       -> specializationOptions
 */
import {
  degreeInterestOptions,
  focusOptions,
  specializationOptions,
} from '../data/degrees'
import {
  decodeHighestEducation,
  decodeStartTimeframe,
  normalizeGradYear,
} from '../services/mediaMatchers/params'

export interface MatchParams {
  degreeInterest?: string
  areaStudy?: string
  mProgram?: string
}

/** Widget state shape, using the field names in `useFormState`. */
export interface MatchPrefill {
  degreeInterest: string
  areaOfStudy: string
  specialization: string
}

/** Reads match parameters off the current URL. */
export function readMatchParams(search?: string): MatchPrefill {
  const params = new URLSearchParams(
    search ?? (typeof window === 'undefined' ? '' : window.location.search),
  )
  return {
    degreeInterest: params.get('degreeInterest')?.trim() || '',
    areaOfStudy: params.get('areaStudy')?.trim() || '',
    specialization: params.get('mProgram')?.trim() || '',
  }
}

/**
 * Resolves the human-readable labels behind the parameter values, so a page can
 * title itself after the exact program the visitor arrived looking for.
 *
 * Also back-fills the area of study from the program when only `mProgram` is
 * given, since every program belongs to exactly one area.
 */
export function resolveMatchPrefill(prefill: MatchPrefill): {
  degreeInterest: string
  areaOfStudy: string
  specialization: string
  degreeLabel: string
  areaLabel: string
  programLabel: string
} {
  const program = prefill.specialization
    ? specializationOptions.find((s) => s.value === prefill.specialization)
    : undefined
  const areaOfStudy = prefill.areaOfStudy || program?.focusValue || ''
  const degree = prefill.degreeInterest
    ? degreeInterestOptions.find((d) => d.value === prefill.degreeInterest)
    : undefined
  const area = areaOfStudy
    ? focusOptions.find((f) => f.value === areaOfStudy)
    : undefined

  return {
    // Drop values that don't resolve, so a typo'd param can't select nothing
    // in the widget or filter the listings down to zero results.
    degreeInterest: degree?.value || '',
    areaOfStudy: area?.value || '',
    specialization: program?.value || '',
    degreeLabel: degree?.type || '',
    areaLabel: area?.type || '',
    programLabel: program?.type || '',
  }
}

/**
 * Builds the page headline from whatever context the URL supplied, falling back
 * to the category name. An explicit `?headline=` always wins.
 */
export function buildMatchHeadline(
  labels: { degreeLabel: string; areaLabel: string; programLabel: string },
  fallbackName: string,
  override?: string,
): string {
  if (override) return override
  const { degreeLabel, programLabel, areaLabel } = labels
  const subject = programLabel || areaLabel || fallbackName
  if (degreeLabel) return `Online ${degreeLabel} in ${subject}`
  return `Online ${subject} Degrees`
}

/**
 * Reads the profile answers a campaign link can pre-fill, accepting either the
 * partner's numeric code or the form's own slug.
 *
 *   ?highestEducation=5&gradYear=2008&startTimeframe=2&military=3
 */
export function readProfileParams(search?: string): {
  currentEducation?: string
  gradYear?: string
  startDate?: string
  isMilitary?: string
} {
  const params = new URLSearchParams(
    search ?? (typeof window === 'undefined' ? '' : window.location.search),
  )
  const out: {
    currentEducation?: string
    gradYear?: string
    startDate?: string
    isMilitary?: string
  } = {}

  const education = decodeHighestEducation(
    params.get('highestEducation')?.trim() || '',
  )
  if (education) out.currentEducation = education

  const rawYear = params.get('gradYear')?.trim() || ''
  if (rawYear) {
    // Invalid years are dropped rather than pre-filling something rejected.
    const year = normalizeGradYear(rawYear)
    if (year) out.gradYear = year === '1965' ? 'Before 1965' : year
  }

  const timeframe = decodeStartTimeframe(
    params.get('startTimeframe')?.trim() || '',
  )
  if (timeframe) out.startDate = timeframe

  const military = params.get('military')?.trim() || ''
  if (military) out.isMilitary = military

  return out
}

/** Reads the optional copy overrides that make a URL a standalone landing page. */
export function readCopyOverrides(search?: string): {
  headline?: string
  subhead?: string
} {
  const params = new URLSearchParams(
    search ?? (typeof window === 'undefined' ? '' : window.location.search),
  )
  return {
    headline: params.get('headline')?.trim() || undefined,
    subhead: params.get('subhead')?.trim() || undefined,
  }
}

/** True when the URL carries at least one usable match parameter. */
export function hasMatchParams(prefill: MatchPrefill): boolean {
  return Boolean(
    prefill.degreeInterest || prefill.areaOfStudy || prefill.specialization,
  )
}

/**
 * Appends match parameters to a path, skipping any that are unset so links
 * stay clean when only part of the context is known.
 */
export function buildMatchLink(path: string, match?: MatchParams): string {
  if (!match) return path
  const params = new URLSearchParams()
  if (match.degreeInterest) params.set('degreeInterest', match.degreeInterest)
  if (match.areaStudy) params.set('areaStudy', match.areaStudy)
  if (match.mProgram) params.set('mProgram', match.mProgram)
  const query = params.toString()
  if (!query) return path
  return `${path}${path.includes('?') ? '&' : '?'}${query}`
}
